import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MoreHorizontal, Settings, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { DOCK, RACCOURCIS, RAIL, entreesRail } from "./nav";
import { useDockAction } from "./useDockAction";
import { MicroButton } from "./MicroButton";
import { useOutilsDock, FenetreOutil, BoutonOutilDock } from "@/components/outils/DockOutils";

const ENTREE = Object.fromEntries(RAIL.filter((x) => x !== "sep").map((x) => [x.code, x]));

const Onglet = ({ e }) => (
  <NavLink to={e.path} end={e.path === "/"} data-testid={`mobile-bar-${e.code}`}
    className="flex flex-col items-center justify-center gap-0.5 min-h-[56px] min-w-[44px] text-[11px] font-semibold"
    style={({ isActive }) => ({ color: isActive ? "var(--accent)" : "var(--muted)" })}>
    <e.icon size={21} strokeWidth={1.9} /><span className="truncate max-w-[72px]">{e.code === "validations" ? "Valider" : e.code === "appels" ? "Appels" : e.label}</span>
  </NavLink>
);

const Tuile = ({ icon: Icon, label, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className="at-soft flex flex-col items-center justify-center gap-2 py-3 min-h-[76px] text-xs font-semibold text-center px-1">
    <Icon size={20} />{label}
  </button>
);

export const BarreMobile = () => {
  const { user, deconnexion } = useAuth();
  const navigate = useNavigate();
  const agir = useDockAction();
  const outils = useOutilsDock();
  const [ouvert, setOuvert] = useState(false);
  const [outil, setOutil] = useState(null);
  const entrees = entreesRail(user).filter((x) => x !== "sep");
  const codes = entrees.map((x) => x.code);
  const gauche = [ENTREE.accueil, codes.includes("appels") ? ENTREE.appels : ENTREE.pipeline];
  const droite = codes.includes("validations") ? ENTREE.validations : ENTREE.fiches;
  const reste = entrees.filter((x) => ![...gauche, droite].includes(x));
  const puis = (fn) => () => { setOuvert(false); fn(); };
  return (
    <nav data-testid="mobile-bar" className="md:hidden fixed bottom-0 inset-x-0 z-40 pb-safe" style={{ background: "var(--surface)", borderTop: "1px solid var(--line-douce, var(--line))" }}>
      <div className="grid grid-cols-5 items-center h-[64px] px-1">
        {gauche.map((e) => <Onglet key={e.code} e={e} />)}
        <div className="flex justify-center -mt-8"><MicroButton size={62} testId="mobile-micro-button" /></div>
        <Onglet e={droite} />
        <button type="button" data-testid="mobile-bar-plus" onClick={() => setOuvert(true)} className="flex flex-col items-center justify-center gap-0.5 min-h-[56px] text-[11px] font-semibold" style={{ color: "var(--muted)" }}>
          <MoreHorizontal size={21} /><span>Plus</span>
        </button>
      </div>
      <FenetreOutil outil={outil} onClose={() => setOutil(null)} />
      <Sheet open={ouvert} onOpenChange={setOuvert}>
        <SheetContent side="bottom" className="at-popover !rounded-b-none pb-10 max-h-[85vh] overflow-y-auto" data-testid="mobile-plus-sheet">
          <SheetHeader><SheetTitle className="text-left">Plus</SheetTitle></SheetHeader>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {reste.map((e) => <Tuile key={e.code} icon={e.icon} label={e.label} onClick={puis(() => navigate(e.path))} testId={`plus-${e.code}`} />)}
            {RACCOURCIS.filter((c) => user.navigation.dock.includes(c)).map((c) => <Tuile key={c} icon={DOCK[c].icon} label={DOCK[c].label} onClick={puis(() => agir(c))} testId={`plus-${c}`} />)}
            <Tuile icon={DOCK.rechercher.icon} label="Rechercher" onClick={puis(() => agir("rechercher"))} testId="plus-rechercher" />
            {outils.map((o) => <BoutonOutilDock key={o.id} outil={o} mobile onOuvrir={(x) => { setOuvert(false); setOutil(x); }} />)}
            <Tuile icon={Settings} label="Paramètres" onClick={puis(() => navigate("/parametres/compte"))} testId="plus-parametres" />
            <Tuile icon={LogOut} label="Se déconnecter" onClick={puis(async () => { await deconnexion(); navigate("/connexion"); })} testId="plus-deconnexion" />
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
};
